import GreenButton from "components/greenButton/GreenButton";
import Page from "components/page/Page";
import PageTitle from "components/pageTitle/PageTitle";
import PageWrapper from "components/pageWrapper/PageWrapper";
import { useAppDispatch, useAppSelector } from "hooks";
import { memo, useEffect } from "react";
import { Form, Spinner, Alert } from "react-bootstrap";
import styled from "styled-components";
import * as Yup from "yup";
import { useFormik } from "formik";
import emailSlice from "reducers/emailSlice";
import Helmet from "components/headHelmet/HeadHelmet";
import { RootState } from "store";
import {
  sendContactErrorSelector,
  sendContactSuccessSelector,
  sendingContactSelector,
} from "selectors/emailSelectors";
import { useLocation } from "react-router-dom";
import InfoText from "components/infoText/InfoText";
import { useTranslation } from "react-i18next";

const StyledLabel = styled(Form.Label)`
  color: gray;
`;

const StyledText = styled(Form.Control)<{ $hasError: boolean | undefined }>`
  border-color: ${(props) => (props.$hasError ? "red !important" : "#e8e8ef")};

  &:focus {
    border-color: #28c604;
    box-shadow: none;
  }
`;

const ContactPage = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation("contact");

  const sendingContact = useAppSelector((state: RootState) =>
    sendingContactSelector(state),
  );

  const sendContactError = useAppSelector((state: RootState) =>
    sendContactErrorSelector(state),
  );

  const sendContactSuccess = useAppSelector((state: RootState) =>
    sendContactSuccessSelector(state),
  );

  const location = useLocation();

  useEffect(() => {
    dispatch(emailSlice.actions.clearSendContact());
  }, [dispatch, location]);

  const schema = Yup.object({
    email: Yup.string()
      .email(t("validation.emailRequired"))
      .required(t("validation.emailRequired")),
    message: Yup.string().required(t("validation.messageRequired")),
  });

  const formik = useFormik({
    validationSchema: schema,
    initialValues: {
      email: "",
      message: "",
    },
    onSubmit: (values: any) => {
      dispatch(emailSlice.actions.sendContact(values));
    },
  });

  return (
    <>
      <Helmet>
        <title>{t("metaTitle")}</title>
      </Helmet>
      <Page collapsedView={true} maxWidth={800}>
        <PageWrapper>
          <PageTitle>{t("pageTitle")}</PageTitle>
          <Form onSubmit={formik.handleSubmit} autoComplete="off">
            <InfoText>{t("infoText")}</InfoText>
            <Form.Group className="mb-3" controlId="email">
              <StyledLabel>{t("emailLabel")}</StyledLabel>
              <StyledText
                $hasError={
                  formik.touched.email && formik.errors.email !== undefined
                }
                value={formik.values.email}
                onChange={formik.handleChange}
              />
            </Form.Group>
            <Form.Group className="mb-4" controlId="message">
              <StyledLabel>{t("messageLabel")}</StyledLabel>
              <StyledText
                $hasError={
                  formik.touched.message && formik.errors.message !== undefined
                }
                className="form-control"
                id="message"
                as="textarea"
                value={formik.values.message}
                onChange={formik.handleChange}
              />
            </Form.Group>
            {sendContactError && (
              <Alert variant="danger">{sendContactError}</Alert>
            )}
            {sendContactSuccess && (
              <Alert variant="success">{t("successMessage")}</Alert>
            )}
            {!sendContactSuccess && (
              <GreenButton disabled={sendingContact}>
                {!sendingContact && <span>{t("sendButton")}</span>}
                {sendingContact && <Spinner size="sm" />}
              </GreenButton>
            )}
          </Form>
        </PageWrapper>
      </Page>
    </>
  );
};

export default memo(ContactPage);
